

const InnoDiscountRules = (() => {
  // -------------------------------------------------------------------------
  // Approved copy — one context-neutral string per scenario
  // -------------------------------------------------------------------------
  const MESSAGES = {
    NOT_FOUND: 'Code not found.',
    EXPIRED: 'Code expired.',
    ALREADY_USED: 'Code already used.',
    ALREADY_APPLIED: 'Code already applied.',
    NOT_COMBINABLE: "Code can't be combined with offers.",
    SUBSCRIPTION: 'Code not valid on subscriptions.',
    PRODUCTS_IN_CART: "Code doesn't apply to items in cart.",
    MIN_NOT_MET: (amount) => `Spend ${amount} more to unlock discount.`,
    UNAVAILABLE: 'Code no longer available.',
    SYSTEM_ERROR: 'Code not applied. Try again.',
  };

  // Exact-match campaigns with requirements — maintain per active promo
  const CAMPAIGNS = {
    WINB25: { minimumCents: 5000 },
    // Add active promos here: CODE: { minimumCents }
  };

  // Prefix-matched campaigns — applies to any code starting with the key.
  // Exact CAMPAIGNS matches take precedence over prefixes.
  const CAMPAIGN_PREFIXES = {
    WINB50: { minimumCents: 10000 },
    // Add prefix promos here: PREFIX: { minimumCents }
  };

  // Phase 2 server-side reasons (Admin API lookup) → message keys
  const SERVER_REASONS = {
    expired: 'EXPIRED',
    usage_limit: 'ALREADY_USED',
    customer_specific: 'NOT_FOUND',
    not_combinable: 'NOT_COMBINABLE',
    products_not_eligible: 'PRODUCTS_IN_CART',
    unavailable: 'UNAVAILABLE',
    not_found: 'NOT_FOUND',
  };

  function findCampaign(code) {
    if (CAMPAIGNS[code]) return CAMPAIGNS[code];
    let match = null;
    Object.keys(CAMPAIGN_PREFIXES).forEach((p) => {
      if (!match && code.indexOf(p) === 0) match = CAMPAIGN_PREFIXES[p];
    });
    return match;
  }

  // -------------------------------------------------------------------------
  // Rule evaluation
  //
  // ctx = {
  //   code:            string   — submitted code that failed (raw)
  //   hasSubscription: boolean  — any line has a selling plan allocation
  //   appliedCodes:    string[] — codes already applied
  //   subtotalCents:   number   — current cart/checkout subtotal in cents
  //   serverReason:    string?  — Phase 2 only: key of SERVER_REASONS
  //   systemError:     boolean? — network/API failure applying the code
  //   formatMoney:     (cents) => string  — e.g. 1500 → "$15"
  // }
  // -------------------------------------------------------------------------
  function resolveDiscountError(ctx) {
    const code = (ctx.code || '').trim().toUpperCase();
    const applied = (ctx.appliedCodes || []).map((c) => String(c).toUpperCase());
    const fmt = ctx.formatMoney || ((c) => `$${(c / 100).toFixed(c % 100 === 0 ? 0 : 2)}`);

    // 0. System / network failure applying the code
    if (ctx.systemError) return MESSAGES.SYSTEM_ERROR;

    // 1. Authoritative server reason (Phase 2 Admin API lookup), when available
    if (ctx.serverReason && SERVER_REASONS[ctx.serverReason]) {
      return MESSAGES[SERVER_REASONS[ctx.serverReason]];
    }

    // 2. Code already applied (exact client-side check)
    if (applied.indexOf(code) !== -1) return MESSAGES.ALREADY_APPLIED;

    // 3. Another discount already applied → combinability conflict
    if (applied.length > 0) return MESSAGES.NOT_COMBINABLE;

    // 4. Known campaign with unmet minimum spend (CAMPAIGNS exact, then prefixes)
    const campaign = findCampaign(code);
    if (campaign && ctx.subtotalCents < campaign.minimumCents) {
      return MESSAGES.MIN_NOT_MET(fmt(campaign.minimumCents - ctx.subtotalCents));
    }

    // 5. Subscription in cart — inference: most likely cause of failure.
    //    Phase 2 replaces this with the exact appliesOnSubscription check.
    if (ctx.hasSubscription) return MESSAGES.SUBSCRIPTION;

    // 6. Fallback — reason unknown client-side
    return MESSAGES.NOT_FOUND;
  }

  return { MESSAGES, CAMPAIGNS, CAMPAIGN_PREFIXES, SERVER_REASONS, findCampaign, resolveDiscountError };
})();

// Browser global for the Rebuy Smart Cart (theme asset usage)
if (typeof window !== 'undefined') {
  window.InnoDiscountRules = InnoDiscountRules;

  // Capture the code at submit time so the cart error block can (a) message on
  // the SUBMITTED code, not the live input, and (b) hide the error as soon as
  // the user edits the input. Document-level capture-phase listener — no
  // changes needed to the Rebuy form templates.
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (form && form.classList && form.classList.contains('rebuy-cart__discount-form')) {
        const inp = form.querySelector('input.rebuy-input');
        if (inp) window.__innoLastCode = inp.value;
      }
    },
    true
  );
}

// ESM exports for the checkout UI extension
export const MESSAGES = InnoDiscountRules.MESSAGES;
export const CAMPAIGNS = InnoDiscountRules.CAMPAIGNS;
export const CAMPAIGN_PREFIXES = InnoDiscountRules.CAMPAIGN_PREFIXES;
export const SERVER_REASONS = InnoDiscountRules.SERVER_REASONS;
export const findCampaign = InnoDiscountRules.findCampaign;
export const resolveDiscountError = InnoDiscountRules.resolveDiscountError;
export default InnoDiscountRules.resolveDiscountError;
